import React from 'react';

import { isDefined, isEmpty, silenceError } from '@nfctron/nexus-core';

import { APP_I18N_NAMESPACE } from '@root/lib/constants/common';
import { countryEnumTickets } from '@root/lib/constants/country';
import { useTranslate } from '@root/lib/hooks/useTranslation';
import { type Component } from '@root/lib/types/components';

import { type AddressData, type AddressProps } from './Address.types';

/**
 * Address component
 * @author filipditrich <ditrich@nfctron.com>
 * @constructor
 */
export const Address: Component<AddressProps> = (props) => {
	const { address, ...rest } = props;
	const { t } = useTranslate(APP_I18N_NAMESPACE.COMMON);
	const country = silenceError(() => countryEnumTickets.get(address.countryId), null);
	const addressLines = address.address || [address.address1, address.address2].filter(Boolean).join(', ');
	const cityPostalCode = [address.city, address.postalCode].filter(Boolean).join(', ');

	return (
		<address {...rest}>
			{/* address lines */}
			{!isEmpty(addressLines) && (
				<React.Fragment>
					<span>{addressLines}</span>
					<br />
				</React.Fragment>
			)}
			{/* city, postalCode */}
			{!isEmpty(cityPostalCode) && (
				<React.Fragment>
					<span>{cityPostalCode}</span>
					<br />
				</React.Fragment>
			)}
			{/* country */}
			{isDefined(country) && (
				<span>
					{isDefined(country.Icon) && <country.Icon className="mr-2.5 inline size-3.5" />}
					{t(`constants.country.${country.countryId}.text`)}
				</span>
			)}
		</address>
	);
};

/**
 * Converts address parts into human-readable address string
 * @returns {string}
 */
export const addressToString = <TAddress extends AddressData>(address: TAddress): string => {
	const country = silenceError(() => countryEnumTickets.get(address.countryId), null);
	return [address.address1, address.address2, address.city, address.postalCode, country?.isoChar2].filter(Boolean).join(', ');
};

Address.displayName = 'component/Address';
